import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, Flame } from "lucide-react";
import { JOBBER_BOOKING_URL } from "../lib/brand";

const CONTACT_EMAIL = "fega.omoko.business@gmail.com";
const LAST_UPDATED = "February 21, 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-black text-white mb-4">
      {title}
    </h2>
    <div className="text-[#D4D4D4] leading-relaxed space-y-3 text-[15px]">
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => (
  <div data-testid="privacy-policy-page">
    <section className="pt-24 pb-12 border-b border-white/10 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_#D90429_0%,_transparent_45%)] opacity-15 pointer-events-none" />
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative">
        <div className="inline-flex items-center gap-2 font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">
          <Shield size={14} /> Privacy Policy
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
          Your Trust,<br/>Our Priority.
        </h1>
        <p className="mt-6 text-[#A3A3A3] max-w-2xl">
          Fuego Precision Cleaning Services LLC ("Fuego Precision Detailing", "we", "us") respects your privacy. This policy explains what we collect, why we collect it, and the choices you have.
        </p>
        <p className="mt-2 text-xs text-[#737373]">Last updated: {LAST_UPDATED}</p>
      </div>
    </section>

    <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">

      <Section title="Information We Collect">
        <p>We collect the personal information you voluntarily provide when you:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Fill out a Meta (Facebook or Instagram) lead form.</li>
          <li>Submit a quote request or booking through our website.</li>
          <li>Contact us by phone, text, or email.</li>
        </ul>
        <p>The information we typically collect includes your <strong className="text-white">name, phone number, email address, service address, vehicle details, and any notes you share about your vehicle or service needs</strong>.</p>
      </Section>

      <Section title="How We Use Your Information">
        <p>We use your information solely to run our business — specifically to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Respond to your quote requests and questions.</li>
          <li>Schedule, confirm, and provide your detailing service.</li>
          <li>Send booking confirmations, appointment reminders, receipts, and follow-up messages.</li>
          <li>Improve the quality of our service.</li>
        </ul>
        <p>We will never contact you for reasons unrelated to the service you requested.</p>
      </Section>

      <Section title="We Do Not Sell Your Information">
        <p>
          <strong className="text-white">We do not sell, rent, or trade your personal information to any third party.</strong> Ever.
        </p>
        <p>Your information may be shared only with the operational tools we use to run our business (for example, Stripe for secure payments, Resend for email delivery, and Jobber for job scheduling and CRM). These services are contractually required to protect your data and use it only to provide their service to us.</p>
      </Section>

      <Section title="Data Security">
        <p>We use industry-standard practices (encrypted connections, secure payment processors, restricted admin access) to keep your information safe. Payments are handled entirely by Stripe — we never see or store your full card number.</p>
      </Section>

      <Section title="Your Rights — Update or Delete Your Information">
        <p>
          You are always in control. If you would like to <strong className="text-white">access, correct, or permanently delete</strong> the personal information we have about you, simply reach out and we'll take care of it.
        </p>
        <div className="mt-4 border border-[#262626] bg-[#0a0a0a] p-5 flex items-center gap-4">
          <div className="w-10 h-10 bg-[#D90429]/15 border border-[#D90429]/40 flex items-center justify-center">
            <Mail size={18} className="text-[#D90429]" />
          </div>
          <div>
            <div className="font-display uppercase tracking-widest text-[10px] text-[#D90429]">Contact Us</div>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Privacy%20Request`}
              data-testid="privacy-contact-email"
              className="text-white font-bold underline decoration-[#D90429] underline-offset-4 hover:text-[#EF233C] transition-colors"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
        <p className="mt-4">We'll respond to your request within 30 days.</p>
      </Section>

      <Section title="Cookies & Analytics">
        <p>Our website uses basic session cookies to keep you logged in (only relevant if you access the admin area) and to support secure payment flows. We do not use invasive tracking or advertising cookies on our site.</p>
      </Section>

      <Section title="Children's Privacy">
        <p>Our services are intended for adults. We do not knowingly collect information from anyone under the age of 13.</p>
      </Section>

      <Section title="Changes to This Policy">
        <p>We may update this policy occasionally. When we do, we'll update the "Last updated" date at the top of the page. Continued use of our services after updates means you accept the revised policy.</p>
      </Section>

      <div className="mt-16 border-2 border-[#D90429] bg-[#D90429]/10 p-8 text-center">
        <h3 className="font-display uppercase tracking-tight text-2xl font-black">Ready to book?</h3>
        <p className="mt-2 text-[#D4D4D4] text-sm">Fully secure. No spam. Ever.</p>
        <a
          href={JOBBER_BOOKING_URL}
          data-testid="privacy-book-cta"
          className="mt-5 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-8 py-4 transition-colors"
        >
          <Flame size={16}/> Book Your Appointment
        </a>
      </div>

      <div className="mt-12 text-center">
        <Link to="/" data-testid="privacy-home-link" className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#D90429] transition-colors">
          ← Back to Home
        </Link>
      </div>
    </div>
  </div>
);

export default PrivacyPolicyPage;
